import { Link } from "react-router-dom";
import { AlertTriangle, ShieldAlert, FileWarning, ScrollText } from "lucide-react";
import { SEO } from "../components/SEO";

/**
 * Volledige juridische disclaimer voor Debate Engine.
 * Drie zichtbaarheidsniveaus:
 *   1. Micro (footer): "Debate Engine kan zich vergissen. Geen rechten te ontlenen."
 *   2. Inline (DiscussionPage synthesis): korte AI-disclaimer met link
 *   3. Volledig (hier op /disclaimer): juridische tekst
 */
export const DisclaimerPage = () => (
  <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12" data-testid="disclaimer-page">
    <SEO
      title="Disclaimer — Debate Engine"
      description="Juridische disclaimer van Debate Engine. AI-output is geen professioneel advies. Geen rechten te ontlenen aan de gegenereerde analyses."
      path="/disclaimer"
    />
    <div className="flex items-center gap-3 mb-8">
      <ScrollText className="w-7 h-7 text-[#2d8cf0]" />
      <h1 className="text-3xl font-black" style={{ fontFamily: "Chivo, sans-serif" }}>
        Disclaimer
      </h1>
    </div>

    <p className="text-sm text-[#52525B] mb-8">
      Laatst bijgewerkt: juni 2026 — versie 1.4
    </p>

    <div className="space-y-6 text-sm leading-relaxed text-[#27272A]">
      <section>
        <p>
          Debate Engine is een AI-gestuurd debatplatform van <strong>An RBG-LLM project</strong> (juridisch: BoPeDo, KvK 92541488),
          ontwikkeld om meerdere perspectieven op vraagstukken inzichtelijk te maken. Hoewel wij ons inspannen om
          kwalitatief hoogstaande, genuanceerde en goed onderbouwde analyses te leveren via meerdere AI-modellen
          (OpenAI GPT-4o, Anthropic Claude Sonnet, Google Gemini, Perplexity Sonar), zijn dit Large Language Models
          die kunnen vergissen, verouderde informatie kunnen bevatten, of feitelijke onjuistheden kunnen produceren.
        </p>
      </section>

      <section className="bg-amber-50 border-l-4 border-amber-400 p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="font-bold text-base mb-2">Geen professioneel advies</h2>
            <p>
              De output van Debate Engine is bedoeld voor verkennende, educatieve en discussie-ondersteunende
              doeleinden. Het vormt nadrukkelijk <strong>geen</strong> medisch, juridisch, financieel, fiscaal of
              ander professioneel advies. Raadpleeg altijd een gekwalificeerde professional voor beslissingen met
              juridische, financiële, medische of veiligheidsimpact.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-red-50 border-l-4 border-red-400 p-5">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="font-bold text-base mb-2">Geen rechten te ontlenen</h2>
            <p>
              Aan de output van Debate Engine, inclusief synthese, verificatie, en argumentanalyse, kunnen
              <strong> geen rechten</strong> worden ontleend. An RBG-LLM project (BoPeDo) aanvaardt geen aansprakelijkheid
              voor beslissingen genomen op basis van de output.
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className="flex items-start gap-3">
          <FileWarning className="w-5 h-5 text-[#2d8cf0] flex-shrink-0 mt-1" />
          <div>
            <h2 className="font-bold text-base mb-2">Eigen verantwoordelijkheid</h2>
            <p>
              Gebruikers worden uitdrukkelijk verzocht eigen kritisch oordeel te blijven hanteren en feitelijke
              claims onafhankelijk te verifiëren, met name op gebieden waar actuele gegevens cruciaal zijn
              (politiek, gezondheidszorg, wet- en regelgeving, financiële markten).
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-bold text-base mb-2">Kennis-actualiteit</h2>
        <p>
          De onderliggende AI-modellen zijn getraind tot een specifieke datum (modelafhankelijk). Voor vraagstukken
          over recente gebeurtenissen kan informatie verouderd zijn. Waar mogelijk gebruiken we live-verificatie
          via externe bronnen, maar volledige actualiteit kan niet worden gegarandeerd.
        </p>
      </section>

      <section className="bg-blue-50 border-l-4 border-[#002FA7] p-5">
        <div className="flex items-start gap-3">
          <FileWarning className="w-5 h-5 text-[#002FA7] flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="font-bold text-base mb-2">B2B Professionele Teams — alleen indicatief</h2>
            <p>
              De vier B2B-teams (Medisch MDO, Juridisch &amp; Compliance, Crisis &amp; Incident, Bouw/Infra
              &amp; Omgevingsvisie) zijn nadrukkelijk <strong>educatief en oriënterend</strong>. Zij vervangen
              <strong> nooit </strong>het oordeel van een arts, advocaat, crisiscoördinator, bouwadviseur of
              andere gekwalificeerde vakprofessional. Beslissingen op basis van deze teams blijven volledig
              voor eigen rekening en risico van de gebruiker.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="font-bold text-base mb-2">Vertaling</h2>
        <p>
          Bij vertalingen van deze disclaimer geldt de Nederlandse versie als bindend.
        </p>
      </section>

      <section>
        <h2 className="font-bold text-base mb-2">Contact</h2>
        <p>
          Heb je vragen over deze disclaimer? Mail naar{" "}
          <a href="mailto:info@debate-engine.com" className="text-[#2d8cf0] hover:underline">
            info@debate-engine.com
          </a>{" "}
          of bezoek onze{" "}
          <Link to="/contact" className="text-[#2d8cf0] hover:underline">
            contactpagina
          </Link>.
        </p>
      </section>
    </div>

    <div className="mt-10 pt-6 border-t border-[#E5E5E5] text-xs text-[#52525B]">
      <p>
        Zie ook:{" "}
        <Link to="/legal" className="text-[#2d8cf0] hover:underline">
          Privacy & Voorwaarden
        </Link>
      </p>
    </div>
  </div>
);

/**
 * Compact inline disclaimer voor onder synthese-output.
 * Toont waarschuwing + link naar volledige disclaimer.
 */
export const InlineDisclaimer = () => (
  <div className="mt-4 bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 flex items-start gap-2 no-print" data-testid="inline-disclaimer">
    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
    <p>
      Debate Engine is een AI-tool die kan vergissen. Aan deze output zijn geen rechten te ontlenen.{" "}
      <Link to="/disclaimer" className="underline font-bold hover:text-amber-700">
        Lees volledige disclaimer
      </Link>.
    </p>
  </div>
);

export default DisclaimerPage;
